import { useEffect, useRef, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { API, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import VideoPlayer from "@/components/VideoPlayer";
import {
  Loader2, Play, Download, Send, Crop, ImagePlus, Music, Clapperboard,
  Scissors, ArrowLeft, Wand2, Upload, Check,
} from "lucide-react";

const ASPECTS = [
  { v: "9:16", label: "Reel 9:16" },
  { v: "1:1", label: "Square 1:1" },
  { v: "16:9", label: "Wide 16:9" },
];

export default function Editor() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isAdmin } = useAuth();
  const [project, setProject] = useState(null);
  const [source, setSource] = useState(null);
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);
  const pollRef = useRef();

  const loadProject = useCallback(async () => {
    const { data } = await api.get(`/projects/${id}`);
    setProject(data);
    setCfg(data.config);
    const assets = await api.get("/assets");
    setSource(assets.data.find((a) => a.id === data.source_asset_id));
    return data;
  }, [id]);

  useEffect(() => { loadProject(); return () => clearInterval(pollRef.current); }, [loadProject]);

  const set = (k, v) => setCfg((c) => ({ ...c, [k]: v }));

  const saveConfig = async (silent = false) => {
    setSaving(true);
    try {
      await api.put(`/projects/${id}/config`, cfg);
      if (!silent) toast.success("Saved");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const uploadAsset = async (file, kind, field) => {
    const fd = new FormData();
    fd.append("file", file);
    fd.append("kind", kind);
    try {
      const { data } = await api.post("/assets/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      set(field, data.id);
      toast.success(`${kind} added`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const startProcess = async () => {
    await saveConfig(true);
    try {
      await api.post(`/projects/${id}/process`);
      setProject((p) => ({ ...p, status: "processing" }));
      toast.info("Rendering your branded reel…");
      pollRef.current = setInterval(async () => {
        const d = await loadProject();
        if (d.status === "ready") { clearInterval(pollRef.current); toast.success("Render complete!"); }
        if (d.status === "failed") { clearInterval(pollRef.current); toast.error("Render failed: " + (d.error || "")); }
      }, 3000);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const download = () => {
    const token = localStorage.getItem("rf_token");
    window.open(`${API}/assets/${project.output_asset_id}/download?auth=${token}`, "_blank");
  };

  if (!project || !cfg)
    return <div className="flex h-screen items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-[#00E5FF]" /></div>;

  const processing = project.status === "processing";
  const ready = project.status === "ready";
  const previewAssetId = ready ? project.output_asset_id : project.source_asset_id;

  return (
    <div className="flex min-h-screen flex-col">
      <header className="flex items-center justify-between border-b border-white/10 bg-[#0a0a0a] px-6 py-3">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate("/library")} data-testid="back-btn" className="rounded-md p-2 text-gray-400 hover:bg-white/5 hover:text-white">
            <ArrowLeft className="h-4 w-4" />
          </button>
          <div>
            <h1 className="font-heading text-lg font-semibold leading-tight">{project.name}</h1>
            <span className="text-xs text-gray-500">{ready ? "Rendered · Ready" : processing ? "Rendering…" : "Draft"}</span>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => saveConfig()} disabled={saving} data-testid="save-btn" className="flex items-center gap-1.5 rounded-md border border-white/10 px-3 py-2 text-sm font-medium hover:bg-white/5 disabled:opacity-60">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />} Save
          </button>
          <button onClick={startProcess} disabled={processing} data-testid="render-btn" className="flex items-center gap-1.5 rounded-md bg-[#00E5FF] px-4 py-2 text-sm font-semibold text-black hover:bg-[#33EBFF] disabled:opacity-60">
            {processing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Wand2 className="h-4 w-4" />} {processing ? "Rendering" : "Render Preview"}
          </button>
          {isAdmin && ready && (
            <>
              <button onClick={download} data-testid="editor-download-btn" className="flex items-center gap-1.5 rounded-md border border-white/10 px-3 py-2 text-sm font-medium text-[#00E5FF] hover:bg-white/5">
                <Download className="h-4 w-4" /> Export
              </button>
              <button onClick={() => navigate(`/publish?project=${id}`)} data-testid="editor-publish-btn" className="flex items-center gap-1.5 rounded-md bg-[#FF007F] px-4 py-2 text-sm font-semibold text-white hover:bg-[#ff3399]">
                <Send className="h-4 w-4" /> Publish
              </button>
            </>
          )}
        </div>
      </header>

      <div className="flex flex-1">
        {/* Left tools */}
        <div className="w-80 shrink-0 space-y-6 overflow-y-auto border-r border-white/10 bg-[#0a0a0a] p-5" style={{ maxHeight: "calc(100vh - 57px)" }}>
          <Section icon={Crop} title="Aspect Ratio">
            <div className="grid grid-cols-3 gap-2">
              {ASPECTS.map((a) => (
                <button key={a.v} onClick={() => set("aspect", a.v)} data-testid={`aspect-${a.v}`}
                  className={`rounded-md border px-2 py-2 text-xs font-medium transition-colors ${cfg.aspect === a.v ? "border-[#00E5FF] bg-[#00E5FF]/10 text-[#00E5FF]" : "border-white/10 text-gray-400 hover:bg-white/5"}`}>
                  {a.v}
                </button>
              ))}
            </div>
          </Section>

          <Section icon={Scissors} title="Trim (seconds)">
            <div className="grid grid-cols-2 gap-3">
              <NumberInput label="Start" value={cfg.trim_start ?? ""} onChange={(v) => set("trim_start", v === "" ? null : parseFloat(v))} testId="trim-start" max={source?.duration} />
              <NumberInput label="End" value={cfg.trim_end ?? ""} onChange={(v) => set("trim_end", v === "" ? null : parseFloat(v))} testId="trim-end" max={source?.duration} />
            </div>
            {source?.duration ? <p className="mt-2 text-xs text-gray-500">Clip length: {source.duration}s</p> : null}
          </Section>

          <Section icon={ImagePlus} title="Logo / Watermark">
            <FilePick accept="image/*" testId="logo-upload" active={!!cfg.logo_asset_id} label="Upload logo (PNG)" onFile={(f) => uploadAsset(f, "logo", "logo_asset_id")} />
            {cfg.logo_asset_id && (
              <div className="mt-3 space-y-3">
                <Slider label="Horizontal" value={cfg.logo_x} min={0} max={100} onChange={(v) => set("logo_x", v)} testId="logo-x" />
                <Slider label="Vertical" value={cfg.logo_y} min={0} max={100} onChange={(v) => set("logo_y", v)} testId="logo-y" />
                <Slider label="Size" value={Math.round(cfg.logo_scale * 100)} min={5} max={60} onChange={(v) => set("logo_scale", v / 100)} testId="logo-scale" />
                <Slider label="Opacity" value={Math.round(cfg.logo_opacity * 100)} min={10} max={100} onChange={(v) => set("logo_opacity", v / 100)} testId="logo-opacity" />
              </div>
            )}
          </Section>

          <Section icon={Music} title="Audio">
            <FilePick accept="audio/*" testId="audio-upload" active={!!cfg.audio_asset_id} label="Upload music/audio" onFile={(f) => uploadAsset(f, "audio", "audio_asset_id")} />
            {cfg.audio_asset_id && (
              <div className="mt-3 space-y-3">
                <div className="grid grid-cols-2 gap-2">
                  {[["background", "Mix"], ["replace", "Replace"]].map(([v, l]) => (
                    <button key={v} onClick={() => set("audio_mode", v)} data-testid={`audio-mode-${v}`}
                      className={`rounded-md border px-2 py-1.5 text-xs font-medium ${cfg.audio_mode === v ? "border-[#00E5FF] bg-[#00E5FF]/10 text-[#00E5FF]" : "border-white/10 text-gray-400 hover:bg-white/5"}`}>
                      {l}
                    </button>
                  ))}
                </div>
                <Slider label="Volume" value={Math.round(cfg.audio_volume * 100)} min={0} max={200} onChange={(v) => set("audio_volume", v / 100)} testId="audio-volume" />
              </div>
            )}
          </Section>

          <Section icon={Clapperboard} title="Intro / Outro">
            <FilePick accept="video/*" testId="intro-upload" active={!!cfg.intro_asset_id} label="Upload intro clip" onFile={(f) => uploadAsset(f, "intro", "intro_asset_id")} />
            <div className="h-2" />
            <FilePick accept="video/*" testId="outro-upload" active={!!cfg.outro_asset_id} label="Upload outro clip" onFile={(f) => uploadAsset(f, "outro", "outro_asset_id")} />
          </Section>
        </div>

        {/* Center preview */}
        <div className="flex flex-1 flex-col items-center justify-center rf-grid-bg p-8">
          <div className="mb-4 flex items-center gap-2 text-sm text-gray-400">
            <Play className="h-4 w-4 text-[#00E5FF]" />
            {ready ? "Rendered output" : "Source preview"} · {cfg.aspect}
          </div>
          <div
            className="overflow-hidden rounded-lg border border-white/10 bg-black rf-glow"
            style={{ height: "62vh", aspectRatio: cfg.aspect.replace(":", "/") }}
            data-testid="preview-frame"
          >
            {processing ? (
              <div className="flex h-full w-full flex-col items-center justify-center gap-3 text-gray-400">
                <Loader2 className="h-10 w-10 animate-spin text-[#00E5FF]" />
                <span className="text-sm">Rendering with FFmpeg…</span>
              </div>
            ) : project.status === "failed" ? (
              <div className="flex h-full w-full flex-col items-center justify-center gap-2 p-6 text-center text-[#FF3B30]">
                <span className="text-sm">Render failed</span>
                <span className="text-xs text-gray-500">{project.error}</span>
              </div>
            ) : (
              <VideoPlayer assetId={previewAssetId} testId="editor-preview" />
            )}
          </div>
          <p className="mt-4 max-w-md text-center text-xs text-gray-600">
            Adjust tools on the left, then hit <span className="text-[#00E5FF]">Render Preview</span> to burn your branding into the reel.
          </p>
        </div>
      </div>
    </div>
  );
}

function Section({ icon: Icon, title, children }) {
  return (
    <div>
      <div className="mb-3 flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-gray-500">
        <Icon className="h-3.5 w-3.5 text-[#00E5FF]" /> {title}
      </div>
      {children}
    </div>
  );
}

function Slider({ label, value, min, max, onChange, testId }) {
  return (
    <div>
      <div className="mb-1 flex justify-between text-xs text-gray-400">
        <span>{label}</span><span className="font-mono text-gray-300">{value}</span>
      </div>
      <input type="range" min={min} max={max} value={value} data-testid={testId}
        onChange={(e) => onChange(parseInt(e.target.value))} className="w-full cursor-pointer" />
    </div>
  );
}

function NumberInput({ label, value, onChange, testId, max }) {
  return (
    <div>
      <label className="mb-1 block text-xs text-gray-400">{label}</label>
      <input type="number" step="0.1" min="0" max={max} value={value} data-testid={testId}
        onChange={(e) => onChange(e.target.value)}
        className="h-9 w-full rounded-md border border-white/10 bg-[#1A1A1A] px-2 text-sm text-white focus:border-[#00E5FF] focus:outline-none" />
    </div>
  );
}

function FilePick({ accept, onFile, label, active, testId }) {
  const ref = useRef();
  return (
    <>
      <input ref={ref} type="file" accept={accept} hidden data-testid={testId} onChange={(e) => e.target.files?.[0] && onFile(e.target.files[0])} />
      <button onClick={() => ref.current?.click()}
        className={`flex w-full items-center gap-2 rounded-md border px-3 py-2.5 text-sm transition-colors ${active ? "border-[#34C759]/40 bg-[#34C759]/10 text-[#34C759]" : "border-white/10 text-gray-400 hover:bg-white/5"}`}>
        {active ? <Check className="h-4 w-4" /> : <Upload className="h-4 w-4" />}
        {active ? "Added — replace" : label}
      </button>
    </>
  );
}
